import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, MapPin, Clock, CheckCircle, Send, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { careers, businessInfo } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const CareersPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Careers', url: 'https://basinplumbing.com/careers/' }
  ];

  const benefits = [
    'Competitive pay',
    'Health insurance',
    'Paid time off',
    'Company vehicle',
    'Tool allowance',
    'Retirement plan',
    'Ongoing training',
    'Growth opportunities'
  ];

  return (
    <>
      <Seo {...seoConfig.careers} />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438]">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Careers</span>
            </nav>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-[#e85a24] rounded-xl flex items-center justify-center">
                <Briefcase className="text-white" size={28} />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Join Our Team
              </h1>
            </div>
            <p className="text-xl text-blue-100 max-w-2xl">
              Build your career with Basin Plumbing. We're always looking for talented 
              individuals to join our growing team in the Permian Basin.
            </p>
          </div>
        </section>

        {/* Why Work With Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1a3a5c] mb-6">
                  Why Work With Basin Plumbing?
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  At Basin Plumbing, we believe our team is our greatest asset. We offer 
                  competitive compensation, excellent benefits, and a supportive work 
                  environment where you can grow your skills and advance your career.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="text-[#e85a24] flex-shrink-0" size={20} />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img 
                  src="/images/plumber-hydro-jetting.jpg" 
                  alt="Basin Plumbing plumber hydro jetting a sewer line"
                  className="rounded-2xl shadow-xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#1a3a5c] rounded-xl p-6 shadow-xl text-white">
                  <div className="flex items-center gap-3">
                    <Users size={32} />
                    <div>
                      <p className="text-2xl font-bold">25+</p>
                      <p className="text-blue-200 text-sm">Team Members</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4">Open Positions</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Explore our current job openings and find the right opportunity for you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {careers.map((job, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-[#1a3a5c] mb-2">{job.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {job.type}
                          </span>
                        </div>
                      </div>
                      <span className="bg-[#e85a24]/10 text-[#e85a24] px-3 py-1 rounded-full text-sm font-medium">
                        Hiring
                      </span>
                    </div>
                    <p className="text-gray-600 mb-6">{job.description}</p>
                    <Link to="/contact">
                      <Button className="w-full bg-[#1a3a5c] hover:bg-[#2a5a8c] text-white">
                        Apply Now
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* General Application */}
            <div className="mt-12 text-center">
              <Card className="max-w-2xl mx-auto border-0 shadow-lg bg-[#1a3a5c]">
                <CardContent className="p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Don't See Your Position?</h3>
                  <p className="text-blue-100 mb-6">
                    We're always looking for talented individuals. Send us your resume 
                    and we'll keep you in mind for future opportunities.
                  </p>
                  <Link to="/contact">
                    <Button className="bg-[#e85a24] hover:bg-[#d14a14] text-white">
                      <Send className="mr-2" size={18} />
                      Send Your Resume
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default CareersPage;
